import React, { useState } from 'react';
import { Box, Button, Grid, Typography, useTheme } from '@material-ui/core';
import BuzzInput from '../../../components/BuzzInput/BuzzInput';
import useToast from '../../../hooks/useToast';
import useLoader from '../../../hooks/useLoader';
import { apiErrorMessage } from '../../Akademy/SchoolMembers/schoolMembersApi';
import { setSiteLegacySchool } from './websiteApi';
import { normaliseId, validateObjectId } from './objectId';
import EnableLegacySchoolFallback from './EnableLegacySchoolFallback';
import WebsiteBuilderFeatures from './WebsiteBuilderFeatures';
import useFlagTree from './useFlagTree';

const EMPTY_FORM = { siteId: '', legacySchoolId: '' };

// Bordered like the module side nav so the page's sections read as matching cards.
const StepCard = ({ step, children }) => {
  const theme = useTheme();
  return (
    <Box
      border={1}
      borderColor={theme.palette.buzzBorder}
      borderRadius={4}
      p={3}>
      {step && (
        <Typography variant="overline" color="primary" component="div">
          Step {step}
        </Typography>
      )}
      {children}
    </Box>
  );
};

const TeenybeansWebsite = () => {
  const { ErrorToast, SuccessToast, WarningToast } = useToast();
  const { handleLoader } = useLoader();
  // One flag tree for the page: a write in either flag section refreshes both.
  const { canManage, email, tree, reload } = useFlagTree();

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field) => (e) => {
    const { value } = e.target;
    setForm((state) => ({ ...state, [field]: value }));
    setErrors((state) => ({ ...state, [field]: '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (submitting) return;

    // Lowercased because the backend stores and echoes the id lowercased; the echo check
    // below would otherwise flag a successful link entered in upper case.
    const siteId = normaliseId(form.siteId);
    const legacySchoolId = normaliseId(form.legacySchoolId);
    const nextErrors = {
      siteId: validateObjectId(siteId),
      legacySchoolId: validateObjectId(legacySchoolId),
    };
    setErrors(nextErrors);
    if (nextErrors.siteId || nextErrors.legacySchoolId) return;

    setSubmitting(true);
    handleLoader(true);
    setSiteLegacySchool(siteId, legacySchoolId)
      .then((res) => {
        // A 200 is not proof of a write: with the fallback flag off the backend ignores it.
        if (!res || res.legacySchoolId !== legacySchoolId) {
          WarningToast(
            "Not applied: the website_builder.legacy_school_fallback flag is off for this site's school."
          );
          return;
        }
        SuccessToast(res.message || 'Legacy school linked to the site.');
        setForm(EMPTY_FORM);
      })
      .catch((error) => ErrorToast(apiErrorMessage(error)))
      .finally(() => {
        setSubmitting(false);
        handleLoader(false);
      });
  };

  return (
    <Grid container spacing={4} alignItems="flex-start">
      <Grid item xs={12} md={6}>
        <StepCard step={1}>
          <EnableLegacySchoolFallback
            canManage={canManage}
            email={email}
            tree={tree}
            reload={reload}
          />
        </StepCard>
      </Grid>
      <Grid item xs={12} md={6}>
        <StepCard step={2}>
          <Typography variant="h6" component="div">
            <Box fontWeight="fontWeightMedium">
              Link a website to a legacy school
            </Box>
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Leads from the site's contact forms go to this school when the site
            admin's email matches no school.
          </Typography>
          <form onSubmit={handleSubmit} noValidate>
            <BuzzInput
              id="tb-website-site-id"
              name="siteId"
              label="Site ID"
              required
              mt={3}
              value={form.siteId}
              onChange={handleChange('siteId')}
              error={!!errors.siteId}
              helperText={errors.siteId}
            />
            <BuzzInput
              id="tb-website-legacy-school-id"
              name="legacySchoolId"
              label="Legacy School ID"
              required
              mt={3}
              value={form.legacySchoolId}
              onChange={handleChange('legacySchoolId')}
              error={!!errors.legacySchoolId}
              helperText={errors.legacySchoolId}
            />
            <Box mt={4}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={submitting}>
                Link School
              </Button>
            </Box>
          </form>
        </StepCard>
      </Grid>
      {canManage && (
        <Grid item xs={12}>
          <StepCard>
            <WebsiteBuilderFeatures email={email} tree={tree} reload={reload} />
          </StepCard>
        </Grid>
      )}
    </Grid>
  );
};

export default TeenybeansWebsite;
